import { useState, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Session } from '@/api/client';
import {
  buildTimeFromMinutes,
  parseTimeToMinutes,
  calcCoef,
  isSlotOccupiedByOther,
  getSessionAtSlot,
  getMaxDurationAtPosition,
  getStudioIndex,
  SESSION_DRAG_TYPE,
} from '@/schedule/scheduleUtils';
import { computeRestoredSessionPlacement } from '@/schedule/scheduleRestorePlacement';
import { useToast } from '@/contexts/ToastContext';
import { markSessionUpdatedByMutation } from '@/lib/sessionRealtimeDedup';
import { patchProjectListCaches } from '@/lib/projectListQueryCache';
import { clearChatAttachDragGhost } from '@/contexts/ChatAttachDragContext';
import { clearDraggingSession, markSessionDropCommitted } from '@/schedule/scheduleDragState';
import { TOTAL_MINUTES } from '@/schedule/scheduleConstants';
import { hasCopyModifier } from '@/schedule/scheduleModifierKeys';
import { autoFontSizePatchForDuration, sessionDurationMinutes } from '@/schedule/sessionShortDurationFontSize';
import type { GridSelection } from '@/schedule/scheduleTypes';

function hhmmFromSessionField(value: unknown): string | undefined {
  if (value == null) return undefined;
  const s = String(value);
  return s.length >= 5 ? s.slice(0, 5) : s;
}

function applyShortSessionFontToUpdate(
  data: Parameters<typeof api.sessions.update>[1],
  existing?: Session | null
): Parameters<typeof api.sessions.update>[1] {
  if (!('start' in data) && !('end' in data) && !('session_type' in data)) {
    return data;
  }
  const newStart =
    (typeof data.start === 'string' ? data.start.slice(0, 5) : undefined) ?? hhmmFromSessionField(existing?.start);
  const newEnd =
    (typeof data.end === 'string' ? data.end.slice(0, 5) : undefined) ?? hhmmFromSessionField(existing?.end);
  if (!newStart || !newEnd) return data;
  const oldStart = hhmmFromSessionField(existing?.start);
  const oldEnd = hhmmFromSessionField(existing?.end);
  const oldType = existing?.session_type ?? null;
  const newType =
    (typeof data.session_type === 'string' ? data.session_type : undefined) ?? oldType ?? 'запись';
  const typeChanged =
    'session_type' in data &&
    String(oldType ?? '').trim() !== String(newType).trim();
  const durationChanged =
    !!oldStart &&
    !!oldEnd &&
    sessionDurationMinutes(oldStart, oldEnd) !== sessionDurationMinutes(newStart, newEnd);
  if (!typeChanged && !durationChanged) return data;
  const patch = autoFontSizePatchForDuration(newStart, newEnd, newType);
  return patch ? { ...data, ...patch } : data;
}

function applyShortSessionFontToCreate(
  data: Parameters<typeof api.sessions.create>[0]
): Parameters<typeof api.sessions.create>[0] {
  const start = typeof data.start === 'string' ? data.start.slice(0, 5) : undefined;
  const end = typeof data.end === 'string' ? data.end.slice(0, 5) : undefined;
  if (!start || !end) return data;
  const sessionType = typeof data.session_type === 'string' ? data.session_type : null;
  const patch = autoFontSizePatchForDuration(start, end, sessionType);
  return patch ? { ...data, ...patch } : data;
}

interface UseScheduleLogicOptions {
  from: string;
  to: string;
}

type SessionUpdateMutationVars = {
  id: string;
  data: Parameters<typeof api.sessions.update>[1];
  /** Не показывать тост (например, при парном обмене сессий — один общий тост) */
  suppressResultToast?: boolean;
};

/** Сброс ghost и module drag state до optimistic flushSync — иначе источник DnD размонтируется и dragend не снимает ghost. */
function endScheduleSessionDragChrome() {
  clearDraggingSession();
  clearChatAttachDragGhost();
}

function scheduleMutationErrMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function useScheduleLogic({ from, to }: UseScheduleLogicOptions) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [gridSelection, setGridSelection] = useState<GridSelection | null>(null);
  const [editingSession, setEditingSession] = useState<Session | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const queryKey = ['sessions', from, to] as const;

  const applySessionPatchToCacheSync = useCallback(
    (sessionId: string, patch: Partial<Session>) => {
      flushSync(() => {
        queryClient.setQueryData<Session[]>(queryKey, (old) => {
          if (!old) return old;
          return old.map((s) => (s.id === sessionId ? { ...s, ...patch } : s));
        });
      });
    },
    [queryClient, queryKey]
  );

  const syncProjectSessions = useCallback(
    (projectId: string | null) => {
      if (!projectId) return;
      api.sessions.list({ projectId }).then((sessions) => {
        patchProjectListCaches(queryClient, (prev) =>
          prev
            ? prev.map((p) => (p.id === projectId ? { ...p, sessions } : p))
            : prev
        );
      });
    },
    [queryClient]
  );

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: SessionUpdateMutationVars) => {
      const prev = queryClient.getQueryData<Session[]>(queryKey);
      const existing = prev?.find((s) => s.id === id);
      return api.sessions.update(id, applyShortSessionFontToUpdate(data, existing));
    },
    onMutate: async (variables: SessionUpdateMutationVars) => {
      const prev = queryClient.getQueryData<Session[]>(queryKey);
      const session = prev?.find((s) => s.id === variables.id);
      const data = applyShortSessionFontToUpdate(variables.data, session);
      const patch = Object.fromEntries(Object.entries(data).filter(([, v]) => v !== undefined)) as Partial<Session>;
      if (patch.session_type === 'заметка') {
        Object.assign(patch, {
          project_id: null,
          project_name: null,
          project_type: null,
          reader_ids: [],
          reader_id: null,
          reader_name: null,
          readers: [],
          reader_progress: 0,
          coef: 0,
          is_recorded: false,
          is_partial: false,
          stop_page: null,
          session_subtype: null,
          soundengineer_id: null,
          soundengineer_name: null,
          soundengineer_color: null,
          second_project_font_size: null,
          is_canceled: false,
          canceled_note: null,
          canceled_hidden_from_schedule: false,
          cancelled_at: null,
        });
      }
      // Пересчитываем coef оптимистично, если меняется start или end (заметка всегда coef = 0)
      if ('start' in patch || 'end' in patch) {
        const effectiveType =
          (patch.session_type as Session['session_type'] | undefined) ?? session?.session_type ?? 'запись';
        if (effectiveType !== 'заметка') {
          const newStart = (patch.start as string | undefined) ?? (session?.start as string | undefined);
          const newEnd = (patch.end as string | undefined) ?? (session?.end as string | undefined);
          if (newStart && newEnd) {
            patch.coef = calcCoef(newStart, newEnd);
          }
        }
      }
      const wasType = session?.session_type ?? 'запись';
      const nextType = (patch.session_type as Session['session_type'] | undefined) ?? wasType;
      const sessionTypeChanged = 'session_type' in patch && nextType !== wasType;
      if (sessionTypeChanged && (nextType === 'заметка' || wasType === 'заметка')) {
        patch.is_header_hidden = false;
      }
      if ('is_canceled' in patch && patch.is_canceled === false) {
        patch.cancelled_at = null;
      }
      if ('is_canceled' in patch && patch.is_canceled === true && !session?.is_canceled) {
        patch.cancelled_at = new Date().toISOString();
      }
      // Синхронный патч кеша до await cancelQueries: иначе после drop сразу идёт dragend,
      // снимается session-bubble_dragging и один кадр рисуется со старыми top/left.
      // flushSync: dragend идёт в том же синхронном хвосте после drop — без flush подписчики
      // React Query ещё не отрисовали новые top/left.
      applySessionPatchToCacheSync(variables.id, patch);
      await queryClient.cancelQueries({ queryKey });
      return { prev, projectId: session?.project_id ?? null };
    },
    onSuccess: (data, variables, context) => {
      if (!variables.suppressResultToast) showSuccess('Сессия обновлена');
      markSessionUpdatedByMutation(data.id);
      queryClient.setQueryData<Session[]>(queryKey, (old) => {
        if (!old) return old;
        const exists = old.some((s) => s.id === data.id);
        if (!exists) return old;
        return old.map((s) => (s.id === data.id ? { ...s, ...data } : s));
      });
      syncProjectSessions(context?.projectId ?? null);
    },
    onError: (err, _variables, context) => {
      if (context?.prev !== undefined) {
        queryClient.setQueryData(queryKey, context.prev);
      }
      queryClient.invalidateQueries({ queryKey });
      showError(scheduleMutationErrMessage(err));
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof api.sessions.create>[0]) =>
      api.sessions.create(applyShortSessionFontToCreate(data)),
    onMutate: async (data) => {
      const prev = queryClient.getQueryData<Session[]>(queryKey);
      const tempId = `temp-${Date.now()}`;
      const enriched = applyShortSessionFontToCreate(data);
      const sessionType = (enriched.session_type as Session['session_type']) ?? 'запись';
      const optimisticReaderIds = enriched.reader_ids ?? (enriched.reader_id ? [enriched.reader_id] : []);
      const computedCoef = enriched.coef ?? (
        sessionType === 'заметка' ? 0 : calcCoef(enriched.start!, enriched.end!)
      );
      const optimisticSession: Session = {
        id: tempId,
        date: enriched.date!,
        start: enriched.start!,
        end: enriched.end!,
        studio: enriched.studio!,
        project_id: enriched.project_id ?? null,
        project_name: enriched.project_name ?? null,
        project_type: enriched.project_type ?? null,
        readers: enriched.readers ?? [],
        reader_ids: optimisticReaderIds,
        reader_id: enriched.reader_id ?? optimisticReaderIds[0] ?? null,
        reader_name: enriched.reader_name ?? null,
        reader_progress: enriched.reader_progress ?? 0,
        coef: computedCoef,
        is_recorded: enriched.is_recorded ?? false,
        is_partial: enriched.is_partial ?? false,
        is_canceled: enriched.is_canceled ?? false,
        canceled_note: enriched.canceled_note ?? null,
        canceled_hidden_from_schedule: enriched.canceled_hidden_from_schedule ?? false,
        stop_page: enriched.stop_page ?? null,
        session_type: (enriched.session_type as Session['session_type']) ?? 'запись',
        session_subtype: enriched.session_subtype ?? null,
        title: enriched.title ?? null,
        content: enriched.content ?? null,
        soundengineer_id: enriched.soundengineer_id ?? null,
        soundengineer_name: enriched.soundengineer_name ?? null,
        soundengineer_color: enriched.soundengineer_color ?? null,
        font_size: enriched.font_size ?? null,
        second_project_font_size: enriched.second_project_font_size ?? null,
      };
      flushSync(() => {
        queryClient.setQueryData<Session[]>(queryKey, (old) => {
          if (!old) return [optimisticSession];
          return [...old, optimisticSession].sort((a, b) => {
            const d = a.date.localeCompare(b.date);
            if (d !== 0) return d;
            return (a.start as string).slice(0, 5).localeCompare((b.start as string).slice(0, 5));
          });
        });
      });
      await queryClient.cancelQueries({ queryKey });
      return { prev, tempId };
    },
    onSuccess: (session, _variables, context) => {
      showSuccess('Сессия создана');
      const tempId = context?.tempId;
      markSessionUpdatedByMutation(session.id);
      setGridSelection(null);
      queryClient.setQueryData<Session[]>(queryKey, (old) => {
        if (!old) return [session];
        let next = tempId ? old.filter((s) => s.id !== tempId) : old;
        next = next.filter((s) => s.id !== session.id);
        return [...next, session].sort((a, b) => {
          const d = a.date.localeCompare(b.date);
          if (d !== 0) return d;
          return (a.start as string).slice(0, 5).localeCompare((b.start as string).slice(0, 5));
        });
      });
      syncProjectSessions(session.project_id ?? null);
    },
    onError: (err, _variables, context) => {
      if (context?.prev !== undefined) {
        queryClient.setQueryData(queryKey, context.prev);
      } else if (context?.tempId) {
        queryClient.setQueryData<Session[]>(queryKey, (old) =>
          old ? old.filter((s) => s.id !== context.tempId) : old
        );
      }
      queryClient.invalidateQueries({ queryKey });
      showError(scheduleMutationErrMessage(err));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: api.sessions.delete,
    onMutate: async (sessionId) => {
      const prev = queryClient.getQueryData<Session[]>(queryKey);
      const session = prev?.find((s) => s.id === sessionId);
      queryClient.setQueryData<Session[]>(queryKey, (old) =>
        old ? old.filter((s) => s.id !== sessionId) : old
      );
      await queryClient.cancelQueries({ queryKey });
      return { prev, projectId: session?.project_id ?? null };
    },
    onSuccess: (_data, _sessionId, context) => {
      showSuccess('Сессия удалена');
      syncProjectSessions(context?.projectId ?? null);
    },
    onError: (err, _variables, context) => {
      if (context?.prev !== undefined) {
        queryClient.setQueryData(queryKey, context.prev);
      }
      queryClient.invalidateQueries({ queryKey });
      showError(scheduleMutationErrMessage(err));
    },
  });

  const toggleSlot = useCallback((date: string, studioIdx: number, slotIdx: number) => {
    setGridSelection((prev) => {
      if (!prev || prev.date !== date || prev.studioIdx !== studioIdx) {
        return { date, studioIdx, slotIdx, endSlotIdx: slotIdx };
      }
      const slots = new Set(
        prev.slotIdx <= (prev.endSlotIdx ?? prev.slotIdx)
          ? Array.from(
              { length: (prev.endSlotIdx ?? prev.slotIdx) - prev.slotIdx + 1 },
              (_, i) => prev.slotIdx + i
            )
          : []
      );
      if (slots.has(slotIdx)) {
        slots.delete(slotIdx);
        const arr = Array.from(slots).sort((a, b) => a - b);
        if (arr.length === 0) return null;
        return { date, studioIdx, slotIdx: arr[0], endSlotIdx: arr[arr.length - 1] };
      }
      slots.add(slotIdx);
      const arr = Array.from(slots).sort((a, b) => a - b);
      return { date, studioIdx, slotIdx: arr[0], endSlotIdx: arr[arr.length - 1] };
    });
  }, []);

  const handleSlotClick = useCallback(
    (date: string, studioIdx: number, slotIdx: number, addToSelection: boolean) => {
      if (addToSelection) {
        toggleSlot(date, studioIdx, slotIdx);
      } else {
        setGridSelection(null);
      }
    },
    [toggleSlot]
  );

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingSession(null);
  }, []);

  const openDialogForNew = useCallback(
    () => {
      if (!gridSelection) return;
      if (dialogOpen && !editingSession) {
        closeDialog();
        return;
      }
      setEditingSession(null);
      setDialogOpen(true);
    },
    [gridSelection, dialogOpen, editingSession, closeDialog]
  );

  const openDialogForEdit = useCallback(
    (session: Session) => {
      if (dialogOpen && editingSession?.id === session.id) {
        closeDialog();
        return;
      }
      const cached = queryClient.getQueryData<Session[]>(queryKey);
      const latest = cached?.find((s) => s.id === session.id) ?? session;
      setEditingSession(latest);
      setGridSelection(null);
      setDialogOpen(true);
    },
    [dialogOpen, editingSession, closeDialog, queryClient, queryKey]
  );

  const handleSaveSession = useCallback(
    (session: Session) => {
      setGridSelection(null);
      queryClient.setQueryData<Session[]>(queryKey, (old) => {
        if (!old) return old;
        const exists = old.some((s) => s.id === session.id);
        if (exists) {
          return old.map((s) => (s.id === session.id ? session : s));
        }
        return [...old, session].sort((a, b) => {
          const d = a.date.localeCompare(b.date);
          if (d !== 0) return d;
          return (a.start as string).slice(0, 5).localeCompare((b.start as string).slice(0, 5));
        });
      });
      syncProjectSessions(session.project_id ?? null);
    },
    [queryClient, queryKey, syncProjectSessions]
  );

  const handleMoveSession = useCallback(
    async (session: Session, targetDate: string, targetStudioIdx: number, targetSlotIdx: number) => {
      const sessions = queryClient.getQueryData<Session[]>(queryKey) ?? [];
      const startMins = targetSlotIdx * 30;
      const duration = parseTimeToMinutes(session.end as string) - parseTimeToMinutes(session.start as string);
      const maxDuration = getMaxDurationAtPosition(sessions, targetDate, targetStudioIdx, startMins, session.id);
      const actualDuration = Math.min(duration, maxDuration, TOTAL_MINUTES - startMins);
      const roundedDuration = Math.max(30, Math.round(actualDuration / 30) * 30);
      const endMins = Math.min(TOTAL_MINUTES, startMins + roundedDuration);
      const patch: Partial<Session> = {
        date: targetDate,
        start: buildTimeFromMinutes(startMins),
        end: buildTimeFromMinutes(endMins),
        studio: String(targetStudioIdx + 1),
      };

      applySessionPatchToCacheSync(session.id, patch);
      await updateMutation.mutateAsync({
        id: session.id,
        data: patch,
      });
    },
    [updateMutation, queryClient, queryKey, applySessionPatchToCacheSync]
  );

  const handleResizeSession = useCallback(
    async (session: Session, newStartMins: number, newEndMins: number) => {
      await updateMutation.mutateAsync({
        id: session.id,
        data: {
          start: buildTimeFromMinutes(newStartMins),
          end: buildTimeFromMinutes(newEndMins),
        },
      });
    },
    [updateMutation]
  );

  const handleDeleteSession = useCallback(
    async (sessionId: string) => {
      if (!window.confirm('Удалить сессию? Это действие нельзя отменить.')) return;
      await deleteMutation.mutateAsync(sessionId);
    },
    [deleteMutation]
  );

  const handleSessionUpdate = useCallback(
    async (sessionId: string, patch: Parameters<typeof api.sessions.update>[1]) => {
      await updateMutation.mutateAsync({ id: sessionId, data: patch });
    },
    [updateMutation]
  );

  const restoreCanceledSession = useCallback(
    async (session: Session) => {
      const all = queryClient.getQueryData<Session[]>(queryKey) ?? [];
      const placement = computeRestoredSessionPlacement(session, all);
      const patch: Parameters<typeof api.sessions.update>[1] = {
        is_canceled: false,
        canceled_note: null,
        canceled_hidden_from_schedule: false,
        date: placement.date,
        start: placement.start,
        end: placement.end,
        studio: placement.studio,
      };
      await updateMutation.mutateAsync({
        id: session.id,
        data: applyShortSessionFontToUpdate(patch, session),
      });
    },
    [queryClient, queryKey, updateMutation]
  );

  /** Оптимистичное обновление сессии без API — только кеш (для мгновенного UI при слайдере font_size и т.д.) */
  const optimisticSessionUpdate = useCallback(
    (sessionId: string, patch: Parameters<typeof api.sessions.update>[1]) => {
      const filtered = Object.fromEntries(
        Object.entries(patch).filter(([, v]) => v !== undefined)
      ) as Partial<Session>;
      if (Object.keys(filtered).length === 0) return;
      applySessionPatchToCacheSync(sessionId, filtered);
    },
    [applySessionPatchToCacheSync]
  );

  const handleCopySession = useCallback(
    async (session: Session, targetDate: string, targetStudioIdx: number, targetSlotIdx: number) => {
      const sessions = queryClient.getQueryData<Session[]>(queryKey) ?? [];
      const startMins = targetSlotIdx * 30;
      const duration = parseTimeToMinutes(session.end as string) - parseTimeToMinutes(session.start as string);
      const maxDuration = getMaxDurationAtPosition(sessions, targetDate, targetStudioIdx, startMins, '');
      if (maxDuration < 30) return;
      const actualDuration = Math.min(duration, maxDuration, TOTAL_MINUTES - startMins);
      const roundedDuration = Math.max(30, Math.round(actualDuration / 30) * 30);
      const endMins = Math.min(TOTAL_MINUTES, startMins + roundedDuration);

      await createMutation.mutateAsync({
        date: targetDate,
        start: buildTimeFromMinutes(startMins),
        end: buildTimeFromMinutes(endMins),
        studio: String(targetStudioIdx + 1),
        project_id: session.project_id ?? undefined,
        project_name: session.project_name ?? undefined,
        project_type: session.project_type ?? undefined,
        reader_ids: session.readers?.map((r) => r.id) ?? session.reader_ids ?? (session.reader_id ? [session.reader_id] : undefined),
        reader_name: session.reader_name ?? undefined,
        reader_progress: session.reader_progress ?? undefined,
        is_recorded: false,
        is_partial: false,
        session_type: session.session_type ?? 'запись',
        session_subtype: session.session_subtype ?? null,
        title: session.title ?? undefined,
        content: session.content ?? undefined,
        soundengineer_id: session.soundengineer_id ?? undefined,
        soundengineer_name: session.soundengineer_name ?? undefined,
        soundengineer_color: session.soundengineer_color ?? undefined,
      });
    },
    [createMutation, queryClient, queryKey]
  );

  const handleExchangeSessions = useCallback(
    async (sessionA: Session, sessionB: Session) => {
      if (sessionA.is_recorded || sessionB.is_recorded) {
        showError('Нельзя обменивать записанные сессии');
        return;
      }
      const sessions = queryClient.getQueryData<Session[]>(queryKey) ?? [];
      const durationA = parseTimeToMinutes(sessionA.end as string) - parseTimeToMinutes(sessionA.start as string);
      const durationB = parseTimeToMinutes(sessionB.end as string) - parseTimeToMinutes(sessionB.start as string);

      const dateB = (sessionB.date as string).slice(0, 10);
      const studioIdxB = getStudioIndex(sessionB.studio);
      const startA = parseTimeToMinutes(sessionB.start as string);
      const maxDurA = getMaxDurationAtPosition(sessions, dateB, studioIdxB, startA, sessionB.id);
      const durA = Math.min(durationA, maxDurA, TOTAL_MINUTES - startA);
      const newEndA = Math.min(TOTAL_MINUTES, startA + Math.max(30, Math.round(durA / 30) * 30));

      const dateA = (sessionA.date as string).slice(0, 10);
      const studioIdxA = getStudioIndex(sessionA.studio);
      const startB = parseTimeToMinutes(sessionA.start as string);
      const maxDurB = getMaxDurationAtPosition(sessions, dateA, studioIdxA, startB, sessionA.id);
      const durB = Math.min(durationB, maxDurB, TOTAL_MINUTES - startB);
      const newEndB = Math.min(TOTAL_MINUTES, startB + Math.max(30, Math.round(durB / 30) * 30));
      const patchA: Partial<Session> = {
        date: sessionB.date,
        start: buildTimeFromMinutes(startA),
        end: buildTimeFromMinutes(newEndA),
        studio: sessionB.studio,
      };
      const patchB: Partial<Session> = {
        date: sessionA.date,
        start: buildTimeFromMinutes(startB),
        end: buildTimeFromMinutes(newEndB),
        studio: sessionA.studio,
      };

      applySessionPatchToCacheSync(sessionA.id, patchA);
      applySessionPatchToCacheSync(sessionB.id, patchB);

      await Promise.all([
        updateMutation.mutateAsync({
          id: sessionA.id,
          data: patchA,
          suppressResultToast: true,
        }),
        updateMutation.mutateAsync({
          id: sessionB.id,
          data: patchB,
          suppressResultToast: true,
        }),
      ]);
      showSuccess('Сессии обновлены');
    },
    [updateMutation, queryClient, queryKey, showError, showSuccess, applySessionPatchToCacheSync]
  );

  const handleSessionDropOnSession = useCallback(
    (draggedSessionId: string, targetSession: Session, _copy: boolean) => {
      const sessions = queryClient.getQueryData<Session[]>(queryKey) ?? [];
      const draggedSession = sessions.find((s) => s.id === draggedSessionId);
      if (!draggedSession || draggedSession.id === targetSession.id) return;
      markSessionDropCommitted(draggedSession.id);
      handleExchangeSessions(draggedSession, targetSession);
      endScheduleSessionDragChrome();
    },
    [handleExchangeSessions, queryClient, queryKey]
  );

  const handleSlotDrop = useCallback(
    (date: string, studioIdx: number, slotIdx: number, e: React.DragEvent) => {
      e.preventDefault();
      const sessionId = e.dataTransfer.getData(SESSION_DRAG_TYPE) || e.dataTransfer.getData('text/plain');
      if (!sessionId) return;
      const sessions = queryClient.getQueryData<Session[]>(queryKey) ?? [];
      const session = sessions.find((s) => s.id === sessionId);
      if (!session) return;

      const copy = hasCopyModifier(e);
      const targetSession = getSessionAtSlot(sessions, date, studioIdx, slotIdx, sessionId);

      if (session.is_recorded || copy) {
        markSessionDropCommitted(session.id);
        handleCopySession(session, date, studioIdx, slotIdx);
        endScheduleSessionDragChrome();
        return;
      }

      if (targetSession) {
        markSessionDropCommitted(session.id);
        handleExchangeSessions(session, targetSession as Session);
        endScheduleSessionDragChrome();
        return;
      }

      if (isSlotOccupiedByOther(sessions, date, studioIdx, slotIdx, sessionId)) return;
      markSessionDropCommitted(session.id);
      handleMoveSession(session, date, studioIdx, slotIdx);
      endScheduleSessionDragChrome();
    },
    [handleMoveSession, handleCopySession, handleExchangeSessions, queryClient, queryKey]
  );

  return {
    gridSelection,
    setGridSelection,
    handleSlotClick,
    handleSlotDrop,
    handleSessionDropOnSession,
    openDialogForNew,
    openDialogForEdit,
    closeDialog,
    handleSaveSession,
    handleSessionUpdate,
    restoreCanceledSession,
    optimisticSessionUpdate,
    handleResizeSession,
    handleDeleteSession,
    createMutation,
    updateMutation,
    dialogOpen,
    editingSession,
  };
}
